import { useEffect, useState } from "react";
import {
  UserNotice,
  isUserNoticeActive,
  msUntilUserNoticeWindowChange
} from "../utils/userNotice";

// Re-check at least this often even when the next boundary is further away:
// a machine that sleeps or has its clock changed would otherwise keep a
// timer aimed at the wrong instant.
const MAX_TIMER_MS = 60 * 60 * 1000;

/**
 * Whether the notice's `startDateTime`/`endDateTime` window currently
 * includes now. Re-evaluates itself when the window opens or closes, so an
 * open tab shows and hides the notice without a reload or a flag change.
 *
 * Starts false and settles in an effect: the clock is read only on the
 * client, which keeps server and first client render identical.
 */
export const useIsUserNoticeActive = (notice: UserNotice | null): boolean => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!notice) {
      setIsActive(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      const now = Date.now();
      setIsActive(isUserNoticeActive(notice, now));
      const wait = msUntilUserNoticeWindowChange(notice, now);
      if (wait === null) return;
      // +1 so the timer lands on the far side of the boundary.
      timer = setTimeout(check, Math.min(wait + 1, MAX_TIMER_MS));
    };
    check();

    return () => clearTimeout(timer);
  }, [notice]);

  return isActive;
};
